/**
 * TypeScript type definitions aligned with backend Python models
 *
 * These types are derived from:
 * - Project model (project_model.py)
 * - ProjectMember model (project_member_model.py)
 * - API response formats from handlers
 * - Constants from constants.py
 */

// ============================================================================
// ENUMS - Aligned with backend constants.py
// ============================================================================

export type ProjectStatus = 'active' | 'archived' | 'deleted';

export type MemberRole = 'admin' | 'editor' | 'viewer';

export type MemberType = 'alias' | 'ldap' | 'posix';

export type ProjectVisibility = 'private' | 'public';

// ============================================================================
// CORE MODELS - Aligned with backend PynamoDB models
// ============================================================================

/**
 * Project interface - aligned with Project model in project_model.py
 */
export interface Project {
  // Primary key
  projectId: string;

  // Basic attributes
  name: string;
  description: string;
  ownerMemberId: string; // Backend uses ownerMemberId, not ownerId
  status: ProjectStatus;
  visibility: ProjectVisibility;

  // Timestamps (ISO strings from backend)
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;

  // Flags
  isDeleted: boolean;

  // Optional attributes
  templateId?: string;
  tags: string[];

  // Count fields
  documentCount: number;
  columnCount: number;

  // Nested settings (MapAttribute from backend)
  settings: Record<string, any>;

  // User-specific fields (added by backend when user context is available)
  userRole?: MemberRole;
  userPermissions?: RolePermissions;
  joinedAt?: string;
  membershipType?: 'direct' | 'ldap' | 'posix';
  accessType?: 'owner' | 'direct' | 'public';
}

/**
 * Project Member interface - aligned with ProjectMember model
 */
export interface ProjectMember {
  // Primary key (composite)
  projectId: string;
  memberId: string; // Format: "{memberType}:{memberName}"

  // Basic attributes
  memberType: MemberType;
  role: MemberRole;
  status: 'active' | 'inactive';

  // Timestamps
  joinedAt: string;
  invitedAt: string;
  lastAccessedAt?: string;

  // Optional attributes
  invitedBy?: string;

  // Computed permissions (from backend)
  permissions: RolePermissions;
}

/**
 * Role permissions - aligned with ROLE_PERMISSIONS in constants.py
 */
export interface RolePermissions {
  canEdit: boolean;
  canDelete: boolean;
  canShare: boolean;
  canAnalyze: boolean;
  canManageMembers: boolean;
  canChangeSettings: boolean;
}

/**
 * Default role permissions - aligned with ROLE_PERMISSIONS in backend constants.py
 * Used when backend doesn't provide userPermissions
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<MemberRole, RolePermissions> = {
  admin: {
    canEdit: true,
    canDelete: true,
    canShare: true,
    canAnalyze: true,
    canManageMembers: true,
    canChangeSettings: true,
  },
  editor: {
    canEdit: true,
    canDelete: false,
    canShare: false,
    canAnalyze: true,
    canManageMembers: false,
    canChangeSettings: false,
  },
  viewer: {
    canEdit: false,
    canDelete: false,
    canShare: false,
    canAnalyze: false,
    canManageMembers: false,
    canChangeSettings: false,
  },
};

/**
 * Get permissions for a given role
 */
export function getPermissionsForRole(role: MemberRole): RolePermissions {
  return DEFAULT_ROLE_PERMISSIONS[role] || DEFAULT_ROLE_PERMISSIONS.viewer;
}

/**
 * Get effective permissions for a project
 * Uses userPermissions from backend if available, otherwise derives from userRole
 */
export function getEffectivePermissions(project: Project | null): RolePermissions {
  if (!project) {
    return DEFAULT_ROLE_PERMISSIONS.viewer;
  }

  // If backend provides userPermissions, use those
  if (project.userPermissions) {
    return project.userPermissions;
  }

  // If backend provides userRole, derive permissions from role
  if (project.userRole) {
    return getPermissionsForRole(project.userRole);
  }

  // Fallback: viewer permissions (read-only)
  return DEFAULT_ROLE_PERMISSIONS.viewer;
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

/**
 * API Response wrapper - standard format from create_success_response
 */
export interface ApiResponse<T> {
  statusCode: number;
  headers: Record<string, string>;
  body: T;
}

/**
 * API Error response - from create_error_response
 */
export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: string;
  };
}

/**
 * List projects response - from list_projects handler
 */
export interface ListProjectsResponse {
  projects: Project[];
  totalCount: number;
  lastEvaluatedKey?: string;
  hasMore?: boolean;
}

/**
 * Create project request - for create_project handler
 */
export interface CreateProjectRequest {
  name: string;
  description?: string;
  templateId?: string;
  tags?: string[];
}

/**
 * Update project request
 */
export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  status?: ProjectStatus;
  visibility?: ProjectVisibility;
  tags?: string[];
  settings?: Record<string, any>;
}

/**
 * Duplicate project request - for duplicate_project handler
 */
export interface DuplicateProjectRequest {
  name: string;
  description?: string;
  copyDocuments?: boolean;
  copyColumns?: boolean;
}

/**
 * Duplicate project response
 */
export interface DuplicateProjectResponse {
  project: Project;
  columnsCopied: number;
  documentsCopied: number;
}

// ============================================================================
// DOCUMENT TYPES
// ============================================================================

export interface Document {
  documentId: string;
  projectId: string;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  fileType: string;
  status: 'uploaded' | 'processing' | 'processed' | 'error';
  uploadedAt: string;
  processedAt?: string;
  s3Bucket: string;
  s3Key: string;
  extractedTextS3Key?: string;
  tokenCount?: number;
  processingErrors?: string[];
}

export interface UploadDocumentsRequest {
  files: {
    fileName: string;
    fileSize: number;
    contentType: string;
  }[];
}

export interface UploadDocumentsResponse {
  uploadUrls: {
    documentId: string;
    uploadUrl: string;
    fields: Record<string, string>;
  }[];
  documents: Document[];
  uploadedCount: number;
  uploadedDocuments?: Document[]; // For compatibility
  errors?: string[]; // For compatibility
}

export interface ListDocumentsResponse {
  documents: Document[];
  totalCount: number;
}

export interface DeleteDocumentsResponse {
  success: boolean;
  deletedCount: number;
  errors?: string[]; // For compatibility
}

// ============================================================================
// COLUMN TYPES
// ============================================================================

export interface Column {
  columnId: string;
  projectId: string;
  name: string;
  type: 'free-text' | 'single-select';
  description?: string;
  prompt?: string;
  options?: string[];
  maxWords?: number;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create column request
 */
export interface CreateColumnRequest {
  name: string;
  description?: string;
  type: 'free-text' | 'single-select';
  prompt?: string;
  options?: string[];
  maxWords?: number;
}

/**
 * Update column request
 */
export interface UpdateColumnRequest {
  name?: string;
  description?: string;
  prompt?: string;
  options?: string[];
  maxWords?: number;
  order?: number;
}

/**
 * Request for POST /generate-prompt. Supplying `draftPrompt` asks the model
 * to improve that draft instead of writing instructions from scratch.
 */
export interface GeneratePromptRequest {
  name: string;
  type: 'free-text' | 'single-select';
  description?: string;
  options?: string[];
  maxWords?: number;
  draftPrompt?: string;
}

/**
 * Response from POST /generate-prompt. `generated` is false when the model was
 * unavailable and `prompt` is only the basic template.
 */
export interface GeneratePromptResponse {
  prompt: string;
  columnType: 'free-text' | 'single-select';
  columnName: string;
  generated: boolean;
  /** Short explanation when improve mode narrowed the draft's scope. */
  note?: string;
}

/**
 * List columns response
 */
export interface ListColumnsResponse {
  columns: Column[];
  totalCount: number;
}

// ============================================================================
// ANALYSIS TYPES - Aligned with new consolidated RESTful API
// ============================================================================

/**
 * Analysis mode - aligned with backend AnalysisMode enum
 */
export type AnalysisMode = 'full' | 'incremental' | 'retry_failed';

/**
 * Analysis run status
 */
export type AnalysisRunStatus = 'running' | 'completed' | 'failed' | 'cancelled';

/**
 * Model configuration for analysis
 */
export interface ModelConfig {
  modelId?: string;
  temperature?: number;
  maxTokens?: number;
}

/**
 * Analysis run - represents a single analysis execution
 * Aligned with backend AnalysisRun model
 */
export interface AnalysisRun {
  analysisRunId: string;
  projectId: string;
  userId: string;
  status: AnalysisRunStatus;
  startedAt: string;
  completedAt?: string;
  cancelledAt?: string;
  duration?: number;
  modelId: string;
  modelConfig?: ModelConfig;
  totalDocuments: number;
  totalColumns: number;
  documentIds?: string[];
  columnIds?: string[];
  processedDocuments: number;
  failedDocuments: number;
  processedResults: number;
  totalExpectedResults: number;
  progressPercentage?: number;
  estimatedCost?: number;
  actualCost?: number;
  totalTokensUsed?: number;
  errors?: string[];
  failedDocumentIds?: string[];
  executionArn?: string;
  analysisType?: string;
  notes?: string;
}

/**
 * Analysis statistics for a project
 */
export interface AnalysisStatistics {
  totalRuns: number;
  completedRuns: number;
  failedRuns: number;
  runningRuns: number;
  successRate: number;
  totalDocumentsAnalyzed: number;
  totalResultsGenerated: number;
  totalCost: number;
  averageDuration?: number;
  timeRange: {
    days: number;
    startDate: string;
    endDate: string;
  };
  recentRuns: AnalysisRun[];
}

/**
 * Analysis overview response - from GET /projects/{projectId}/analysis
 */
export interface AnalysisOverviewResponse {
  projectId: string;
  status: 'idle' | 'analyzing' | 'completed' | 'failed' | 'cancelled';
  message: string;
  readyForAnalysis: boolean;
  hasDocuments: boolean;
  hasColumns: boolean;
  totalDocuments: number;
  totalColumns: number;
  missingPrerequisites: string[];
  runs: AnalysisRun[];
  totalRuns: number;
  statistics?: AnalysisStatistics;
}

/**
 * Start analysis request - for POST /projects/{projectId}/analysis
 */
export interface StartAnalysisRequest {
  analysisMode?: AnalysisMode;
  documentIds?: string[];
  columnIds?: string[];
  groupIds?: string[];
  modelConfig?: ModelConfig;
  estimatedCost?: number;
  notes?: string;
}

/**
 * Start analysis response - from POST /projects/{projectId}/analysis
 */
export interface StartAnalysisResponse {
  analysisRunId: string;
  status: 'started';
  analysisMode: AnalysisMode;
  totalDocuments: number;
  totalColumns: number;
  totalExpectedResults: number;
  skippedDocuments?: number;
  startedAt: string;
  modelId: string;
  executionArn?: string;
  analysisType?: string;
}

/**
 * Cancel analysis response - from DELETE /projects/{projectId}/analysis/{analysisRunId}
 */
export interface CancelAnalysisResponse {
  analysisRunId: string;
  status: 'cancelled';
  cancelledAt: string;
  processedDocuments: number;
  processedResults: number;
  message: string;
}

/**
 * Analysis request options (legacy - for backward compatibility)
 */
export interface AnalysisRequest {
  documentIds?: string[];
  columnIds?: string[];
}

/**
 * Analysis response when starting analysis (legacy - for backward compatibility)
 */
export interface AnalysisResponse {
  status: 'started';
  totalDocuments: number;
  totalColumns: number;
  startedAt: string;
}

/**
 * Analysis progress status (legacy - for backward compatibility)
 */
export interface AnalysisProgress {
  status: 'analyzing' | 'completed' | 'error';
  totalDocuments: number;
  processedDocuments: number;
  totalColumns: number;
  processedResults: number;
  totalExpectedResults: number;
  progressPercentage?: number;
  startedAt: string;
}

/**
 * Single analysis result for a document-column pair
 */
export interface AnalysisResult {
  answer: string;
  justification: string;
  confidence: number;
  processedAt: string;
}

/**
 * Feedback data for an analysis result
 */
export interface FeedbackData {
  feedbackAccuracy: 'yes' | 'no' | null;
  feedbackComment: string | null;
  feedbackBy: string | null;
  feedbackAt: string | null;
}

/**
 * Analysis result with feedback data
 */
export interface AnalysisResultWithFeedback extends AnalysisResult, FeedbackData {}

/**
 * Response from the PUT feedback endpoint
 */
export interface FeedbackResponse {
  projectId: string;
  documentId: string;
  columnId: string;
  feedbackAccuracy: 'yes' | 'no';
  feedbackComment: string | null;
  feedbackBy: string;
  feedbackAt: string;
}

/**
 * Request body for batch feedback submission (all columns of a document)
 */
export interface BatchFeedbackRequest {
  columns: Record<
    string,
    {
      accuracy: 'yes' | 'no';
      comment?: string;
    }
  >;
}

/**
 * Response from the PUT batch feedback endpoint
 */
export interface BatchFeedbackResponse {
  projectId: string;
  documentId: string;
  feedbackBy: string;
  feedbackAt: string;
  columns: Record<
    string,
    {
      feedbackAccuracy: 'yes' | 'no';
      feedbackComment: string | null;
    }
  >;
}

/**
 * Document results with all column results
 */
export interface DocumentResults {
  documentId: string;
  fileName: string;
  status: string;
  columnResults: Record<string, AnalysisResultWithFeedback>;
  /** Present on group results — the group ID (starts with grp-) */
  groupId?: string;
  /** Present on group results — human-readable group name */
  groupName?: string;
  /** Present on group results — ordered list of member document IDs */
  sourceDocumentIds?: string[];
}

/**
 * Results response with documents and columns
 */
export interface ResultsResponse {
  results: DocumentResults[];
  columns: Column[];
  /** Indicates that the result matrix cannot be loaded safely in the browser. */
  tooLarge?: boolean;
  /** Results are partial because the client-side page cap was hit; coverage is incomplete. */
  truncated?: boolean;
  totalDocuments?: number;
  totalColumns?: number;
  totalCells?: number;
  useExport?: boolean;
  pageSize?: number;
}

/**
 * Export options
 */
export interface ExportOptions {
  includeJustifications?: boolean;
  includeConfidence?: boolean;
  includeVerbatim?: boolean;
  columnIds?: string[];
}

/**
 * Export response with download URL
 */
export interface ExportResponse {
  downloadUrl: string;
  expiresAt: string;
}

/**
 * Existing results check response (legacy - replaced by AnalysisOverviewResponse)
 */
export interface ExistingResultsResponse {
  hasExistingResults: boolean;
  resultCount: number;
  lastAnalyzedAt?: string;
}

// ============================================================================
// TEMPLATE TYPES
// ============================================================================

/**
 * Template interface - aligned with Template model in template_model.py
 */
export interface Template {
  templateId: string;
  userId: string;
  name: string;
  description?: string;
  visibility: 'user' | 'shared';
  columns: TemplateColumn[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Template column definition (subset of Column interface)
 */
export interface TemplateColumn {
  name: string;
  description?: string;
  type: 'free-text' | 'single-select';
  prompt?: string;
  options?: string[];
  maxWords?: number;
}

/**
 * Create template request
 */
export interface CreateTemplateRequest {
  name: string;
  description?: string;
  visibility?: 'user' | 'shared';
  columns: TemplateColumn[];
}

/**
 * Update template request
 */
export interface UpdateTemplateRequest {
  name?: string;
  description?: string;
  visibility?: 'user' | 'shared';
  columns?: TemplateColumn[];
}

/**
 * List templates response
 */
export interface ListTemplatesResponse {
  templates: Template[];
  totalCount: number;
}

// ============================================================================
// MEMBER SHARING TYPES
// ============================================================================

/**
 * Template Member interface - aligned with TemplateMember model
 * Templates only support viewer role
 */
export interface TemplateMember {
  // Primary key (composite)
  templateId: string;
  memberId: string; // Format: "{memberType}:{memberName}"

  // Basic attributes
  memberType: MemberType;
  role: 'viewer'; // Templates only support viewer role
  status: 'active' | 'inactive';

  // Timestamps
  joinedAt: string;
  invitedAt: string;

  // Optional attributes
  invitedBy?: string;

  // Computed permissions
  permissions: Record<string, boolean>;
}

/**
 * List project members response
 */
export interface ListProjectMembersResponse {
  members: ProjectMember[];
  totalCount: number;
}

/**
 * List template members response
 */
export interface ListTemplateMembersResponse {
  members: TemplateMember[];
  totalCount: number;
}

/**
 * Add project member request
 */
export interface AddProjectMemberRequest {
  memberName: string;
  memberType?: MemberType;
  role: MemberRole;
}

/**
 * Add template member request
 */
export interface AddTemplateMemberRequest {
  memberName: string;
  memberType?: MemberType;
}

/**
 * Update project member request
 */
export interface UpdateProjectMemberRequest {
  role: MemberRole;
}

/**
 * Member operation response
 */
export interface MemberOperationResponse {
  success: boolean;
  memberId: string;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Generic paginated response
 */
export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  hasMore: boolean;
  lastEvaluatedKey?: string;
}

/**
 * Generic API operation result
 */
export interface OperationResult {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * File upload progress tracking
 */
export interface UploadProgress {
  fileName: string;
  progress: number;
  stage: 'uploading' | 'processing' | 'complete' | 'error';
  error?: string;
}

// ============================================================================
// TYPE GUARDS - Runtime type checking
// ============================================================================

/**
 * Type guard to check if response is an API error
 */
export function isApiError(response: any): response is ApiError {
  return response && typeof response === 'object' && 'error' in response;
}

/**
 * Type guard to check if project status is valid
 */
export function isValidProjectStatus(status: string): status is ProjectStatus {
  return ['active', 'archived', 'deleted'].includes(status);
}

/**
 * Type guard to check if member role is valid
 */
export function isValidMemberRole(role: string): role is MemberRole {
  return ['admin', 'editor', 'viewer'].includes(role);
}

// ============================================================================
// ADDITIONAL MEMBER REQUEST TYPES - For backward compatibility
// ============================================================================

/**
 * Add member request - for adding members to projects or templates
 */
export interface AddMemberRequest {
  memberName: string;
  memberType?: MemberType;
  role?: MemberRole; // Only for projects, templates are always 'viewer'
}

/**
 * Update member request - for updating project member roles
 */
export interface UpdateMemberRequest {
  role: MemberRole;
}

/**
 * List members response
 */
export interface ListMembersResponse {
  members: ProjectMember[] | TemplateMember[];
  totalCount: number;
}

// ============================================================================
// PEOPLE API TYPES
// ============================================================================

/**
 * Response from GET /people/search — alias autocomplete suggestions
 */
export interface PeopleSearchSuggestion {
  login: string;
  firstName?: string;
  lastName?: string;
}

export interface PeopleSearchResponse {
  suggestions: PeopleSearchSuggestion[];
}

/**
 * Response from GET /people/validate — alias existence check
 * Field names follow PAPI: basicInfo.firstName, basicInfo.lastName
 */
export interface PeopleValidateResponse {
  valid: boolean;
  login?: string;
  firstName?: string;
  lastName?: string;
}

// ============================================================================
// DOCUMENT SPACE TYPES
// ============================================================================

export type SpaceStatus = 'active' | 'archived' | 'deleted';

export type SpaceVisibility = 'private' | 'public';

/**
 * DocumentSpace — a persistent, named collection of documents
 * that lives outside any single project.
 */
export interface DocumentSpace {
  spaceId: string;
  name: string;
  description: string;
  ownerMemberId: string;
  status: SpaceStatus;
  visibility: SpaceVisibility;
  documentCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  isDeleted: boolean;
  userRole?: MemberRole;
  userPermissions?: RolePermissions;
}

/**
 * Document stored in a space — mirrors the project Document interface
 */
export interface SpaceDocument {
  documentId: string;
  spaceId: string;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  fileType: string;
  status: 'uploaded' | 'processing' | 'processed' | 'error';
  uploadedAt: string;
  processedAt?: string;
  s3Bucket: string;
  s3Key: string;
  extractedTextS3Key?: string;
  tokenCount?: number;
}

/**
 * Space member — viewer-only sharing for MVP
 */
export interface SpaceMember {
  spaceId: string;
  memberId: string;
  memberType: MemberType;
  role: 'viewer';
  status: 'active' | 'inactive';
  joinedAt: string;
  invitedAt: string;
  invitedBy?: string;
  permissions: Record<string, boolean>;
}

export interface CreateSpaceRequest {
  name: string;
  description?: string;
}

export interface UpdateSpaceRequest {
  name?: string;
  description?: string;
  status?: SpaceStatus;
  visibility?: SpaceVisibility;
}

export interface ListSpacesResponse {
  spaces: DocumentSpace[];
  totalCount: number;
}

export interface ListSpaceDocumentsResponse {
  documents: SpaceDocument[];
  totalCount: number;
}

export interface SpaceUploadDocumentsResponse {
  uploadUrls: {
    documentId: string;
    uploadUrl: string;
    fields: Record<string, string>;
  }[];
  documents: SpaceDocument[];
  uploadedCount: number;
}

export interface DeleteSpaceDocumentsResponse {
  success: boolean;
  deletedCount: number;
}

export interface ImportFromSpaceRequest {
  spaceId: string;
  documentIds?: string[];
}

export interface ImportFromSpaceResponse {
  importedCount: number;
  documents: Document[];
  failedCount?: number;
  errors?: string[];
}

// ============================================================================
// SPACE COPY JOB TYPES (Async copy operations)
// ============================================================================

export type SpaceCopyJobStatus = 'accepted' | 'running' | 'completed' | 'failed';

export interface SpaceCopyJobResponse {
  jobId: string;
  status: SpaceCopyJobStatus;
  operation: 'import' | 'save';
  projectId: string;
  spaceId: string;
  totalCount: number;
  copiedCount: number;
  failedCount: number;
  errors: string[];
  duplicateAttemptCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ListSpaceMembersResponse {
  members: SpaceMember[];
  totalCount: number;
}

export interface SaveToSpaceResponse {
  savedCount: number;
  failedCount?: number;
  errors?: string[];
}

// ============================================================================
// DOCUMENT GROUP TYPES
// ============================================================================

/**
 * DocumentGroup — a contract family grouping related documents
 * (e.g., MSA + amendments) for combined analysis.
 * Shared by projects and spaces — ownerType distinguishes context.
 */
export interface DocumentGroup {
  groupId: string;
  ownerId?: string;
  ownerType?: string;
  projectId?: string;
  spaceId?: string;
  name: string;
  parentDocumentId: string;
  memberDocumentIds: string[];
  memberCount: number;
  totalTokenCount: number;
  fitsContextWindow: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create document group request
 */
export interface CreateDocumentGroupRequest {
  name: string;
  parentDocumentId: string;
  memberDocumentIds: string[];
}

/**
 * Update document group request
 */
export interface UpdateDocumentGroupRequest {
  name?: string;
  parentDocumentId?: string;
  memberDocumentIds?: string[];
}

/**
 * List document groups response
 */
export interface ListDocumentGroupsResponse {
  groups: DocumentGroup[];
  totalCount: number;
}

// ============================================================================
// AGENT SESSION TYPES
// ============================================================================

export interface AgentSession {
  sessionId: string;
  title: string;
  createdAt: string;
  lastMessageAt: string;
}

export interface AgentSessionMessage {
  requestId: string;
  sessionId: string;
  userMessage: string;
  agentResponse?: string;
  events?: Array<{ type: string; content?: string; tool?: string; summary?: string }>;
  status: string;
  errorMessage?: string;
  createdAt: string;
}
